import React from 'react';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import {
  LANGUAGES,
  isLanguageOn,
  languageName,
  languageSourceLabel,
  nudgeSentence,
} from '../../platform-languages';
import type { LanguageCode, LanguageNudge, LanguageSource } from '../../platform-languages';

/**
 * Where a patient's language is set (amendment 25 §5, board `LangWhere`).
 *
 * Two rules live in this file, because this is the only place in the product that
 * can break them:
 *
 *   1. **ELVA never guesses a language from a name.** There is no detector here
 *      and there must not be one. *Moradi* is not a language, and a practice
 *      discovering that ELVA decided otherwise has a real problem. The language
 *      comes from the PMS or from a person choosing it, and the line under the
 *      select says which.
 *   2. **A language the practice has not switched on sends English, and says so.**
 *      Not a silent fallback — the note names the language and links to the switch,
 *      because otherwise this reads as a setting that took effect.
 */
export interface PatientLanguageSelectProps {
  value: LanguageCode;
  source: LanguageSource;
  /** Only when there is a message the patient actually wrote in another language. */
  nudge?: LanguageNudge | undefined;
  onChange: (next: LanguageCode) => void;
  onAcceptNudge?: ((code: LanguageCode) => void) | undefined;
  onDismissNudge?: (() => void) | undefined;
  /** Opens AI Brain › General card 05, where a language is switched on. */
  onOpenLanguages?: (() => void) | undefined;
  className?: string;
}

export function PatientLanguageSelect({
  value,
  source,
  nudge,
  onChange,
  onAcceptNudge,
  onDismissNudge,
  onOpenLanguages,
  className,
}: PatientLanguageSelectProps) {
  const off = !isLanguageOn(value);
  return (
    <div className={cn('min-w-[190px]', className)}>
      <label
        htmlFor="patient-language"
        className="block font-[var(--font-mono)] text-[10px] font-bold uppercase tracking-[0.14em] text-[var(--ink-4)]"
      >
        Language
      </label>
      <select
        id="patient-language"
        value={value}
        onChange={(e) => {
          onChange(e.target.value as LanguageCode);
        }}
        className="mt-1 w-full rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-2.5 py-1.5 text-[13px] font-semibold text-[var(--ink-0)] outline-none focus:border-[var(--ai-3)]"
      >
        {/* Every language, switched on or not — a patient's language is a fact
            about the patient, and hiding it would lose it. */}
        {LANGUAGES.map((l) => (
          <option key={l.code} value={l.code}>
            {l.name}
            {isLanguageOn(l.code) ? '' : ' (not switched on)'}
          </option>
        ))}
      </select>

      {/* Where it came from. Without this line a staff member cannot tell their
          own colleague's decision from whatever the PMS happened to hold. */}
      <div className="mt-1 text-[11px] text-[var(--ink-4)]">{languageSourceLabel(source)}</div>

      {off ? (
        <div className="mt-1.5 text-[11px] leading-[1.5] text-[var(--gold)]">
          {languageName(value)} isn’t switched on — this patient gets English.{' '}
          <button
            type="button"
            onClick={onOpenLanguages}
            className="font-semibold text-[var(--ai-3)] hover:underline"
          >
            Switch it on
          </button>
        </div>
      ) : null}

      {/* The nudge, and only when it applies: it is built from something the
          patient wrote, never from their name. */}
      {nudge !== undefined && nudge.code !== value ? (
        <div className="mt-2 rounded-[10px] bg-[var(--ai-tint)] px-2.5 py-2">
          <div className="text-[11.5px] leading-[1.5] text-[var(--ink-1)]">{nudgeSentence(nudge)}</div>
          <div className="mt-1.5 flex items-center gap-2">
            <button
              type="button"
              onClick={() => {
                onAcceptNudge?.(nudge.code);
              }}
              className="rounded-[7px] bg-[var(--ai-3)] px-2 py-1 text-[11.5px] font-semibold text-white"
            >
              Make {languageName(nudge.code)}
            </button>
            <button
              type="button"
              onClick={onDismissNudge}
              className="text-[11.5px] font-semibold text-[var(--ink-3)] hover:underline"
            >
              No
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

/**
 * The bulk bar's *Set language* (§5). A family shares a language far more often
 * than they share anything else on this page, which is why setting it one patient
 * at a time was the wrong shape.
 */
export function SetLanguageBulk({
  count,
  onSet,
}: {
  count: number;
  onSet: (code: LanguageCode) => void;
}) {
  const [open, setOpen] = React.useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => {
          setOpen(!open);
        }}
        className="inline-flex items-center gap-1.5 rounded-[9px] border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-1.5 text-[12.5px] font-semibold text-[var(--ink-1)]"
      >
        Set language
        <Icon kind="down" size={11} stroke={2.2} />
      </button>
      {open ? (
        <>
          <div
            className="fixed inset-0 z-[60]"
            onClick={() => {
              setOpen(false);
            }}
            aria-hidden
          />
          <div className="absolute z-[61] mt-1 w-[220px] overflow-hidden rounded-[11px] border border-[var(--cream-2)] bg-[var(--cream-1)] shadow-[var(--sh-2)]">
            <div className="border-b border-[var(--cream-2)] px-3 py-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--ink-4)]">
              {count} selected
            </div>
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                type="button"
                onClick={() => {
                  onSet(l.code);
                  setOpen(false);
                }}
                className="flex w-full items-baseline justify-between gap-3 px-3 py-2 text-left hover:bg-[var(--cream-0)]"
              >
                <span className="text-[12.5px] font-semibold text-[var(--ink-0)]">{l.name}</span>
                <span dir={l.rtl ? 'rtl' : undefined} className="text-[11px] text-[var(--ink-4)]">
                  {l.nativeName}
                </span>
              </button>
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}

/** `Call Maryam Rezaei · speaks Farsi` — what a person needs before dialling (§5). */
export function speaksLine(name: string, code: LanguageCode): string {
  return code === 'en' ? `Call ${name}` : `Call ${name} · speaks ${languageName(code)}`;
}

/**
 * Setting the language for several patients at once (§5, board panel 3).
 *
 * This is a **table of results with checkboxes**, not a card grid: the flow is
 * *search a surname, select the family, set one language*, and a family sharing a
 * language is the single most common case there is. Doing it one chart at a time
 * was how a household ended up half in English.
 */
export function PatientsLanguageTable({
  rows,
  onSetMany,
}: {
  rows: { id: string; name: string; language: LanguageCode }[];
  onSetMany: (ids: string[], code: LanguageCode) => void;
}) {
  const [picked, setPicked] = React.useState<string[]>([]);
  const allPicked = rows.length > 0 && picked.length === rows.length;
  const toggle = (id: string) => {
    setPicked((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };
  if (rows.length === 0) return null;
  return (
    <div className="mb-6 overflow-hidden rounded-[var(--r-lg)] border border-[var(--cream-2)] bg-[var(--cream-1)]">
      <div className="flex flex-wrap items-center gap-3 border-b border-[var(--cream-2)] px-4 py-2.5">
        <label className="inline-flex items-center gap-2 text-[12px] font-semibold text-[var(--ink-2)]">
          <input
            type="checkbox"
            checked={allPicked}
            aria-label="Select every result"
            onChange={() => {
              setPicked(allPicked ? [] : rows.map((r) => r.id));
            }}
          />
          {picked.length === 0 ? 'Select patients' : `${String(picked.length)} selected`}
        </label>
        {/* The bulk bar only exists once something is selected — a Set language
            button with nothing chosen is a button that cannot work. */}
        {picked.length > 0 ? (
          <SetLanguageBulk
            count={picked.length}
            onSet={(code) => {
              onSetMany(picked, code);
              setPicked([]);
            }}
          />
        ) : null}
      </div>
      <div className="grid grid-cols-[28px_1fr_140px] gap-x-3 px-4 py-2 text-[10.5px] font-bold uppercase tracking-[0.08em] text-[var(--ink-4)]">
        <span />
        <span>Patient</span>
        <span>Language</span>
      </div>
      {rows.map((r) => (
        <label
          key={r.id}
          className="grid cursor-pointer grid-cols-[28px_1fr_140px] items-center gap-x-3 border-t border-[var(--cream-2)] px-4 py-2 text-[12.5px] hover:bg-[var(--cream-0)]"
        >
          <input
            type="checkbox"
            checked={picked.includes(r.id)}
            aria-label={`Select ${r.name}`}
            onChange={() => {
              toggle(r.id);
            }}
          />
          <span className="truncate font-semibold text-[var(--ink-0)]">{r.name}</span>
          <span className="text-[var(--ink-2)]">
            {languageName(r.language)}
            {isLanguageOn(r.language) ? '' : ' (not switched on)'}
          </span>
        </label>
      ))}
    </div>
  );
}
